/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedHTMLMessage, FormattedNumber } from 'js/lib/coursera.react-intl';

import { Button, Typography2 } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';

import type { CareerRolesFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import {
  baseSalaryBarWidth,
  getProductSlugs,
  getSalaryBarWidth,
} from 'bundles/front-page/components/modules/career-roles/CareerRolePanel';
import SalaryBar from 'bundles/front-page/components/modules/career-roles/SalaryBar';

import _t from 'i18n!nls/front-page';

type Props = {
  role?: CareerRolesFieldsFragment | null;
  countryCode?: string;
};

const styles = {
  container: css`
    width: 80vw;
    border-radius: var(--cds-border-radius-200);
    padding: var(--cds-spacing-300) var(--cds-spacing-200);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    background: var(--cds-color-neutral-background-primary);
  `,

  heading: css`
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    padding-bottom: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-200);
  `,

  title: css`
    margin-bottom: var(--cds-spacing-100);
  `,

  section: css`
    margin-bottom: var(--cds-spacing-200);
  `,

  salaryHeading: css`
    margin-bottom: var(--cds-spacing-200);
  `,

  salaryBars: css`
    margin-bottom: var(--cds-spacing-150);
  `,

  recommendationWrapper: css`
    margin: calc(var(--cds-spacing-150) * -1) 0;
  `,
};

const CareerRoleCard = ({ role, countryCode }: Props) => {
  if (!role || !role.title) {
    return null;
  }

  const showMedianSalary = role?.mediumSalaryCareerRole && role?.mediumSalaryWorkingProfessional;
  const salaryBarWidth = getSalaryBarWidth(role?.mediumSalaryWorkingProfessional, role?.mediumSalaryCareerRole);
  const productSlugs = getProductSlugs(role);
  const showProductCards = productSlugs?.length;
  const showRecommendations = showProductCards || role?.testimonial;

  return (
    <div css={styles.container}>
      <div css={styles.heading}>
        <Typography2 variant="subtitleLarge" component="h3" css={styles.title}>
          {role.title}
        </Typography2>
        {role.description && (
          <Typography2 variant="bodySecondary" component="div" color="supportText">
            {role.description}
          </Typography2>
        )}
      </div>
      {showMedianSalary && (
        <div css={styles.section}>
          <Typography2 variant="actionSecondary" component="h4" css={styles.salaryHeading}>
            {_t('Median Salary')}
          </Typography2>

          <div css={styles.salaryBars}>
            <SalaryBar
              roleTitle={_t('All occupations')}
              salary={role.mediumSalaryWorkingProfessional}
              footnoteReference="*"
              width={baseSalaryBarWidth}
              variant="base"
              countryCode={countryCode}
            />
            <SalaryBar
              roleTitle={role.title}
              salary={role.mediumSalaryCareerRole}
              footnoteReference="**"
              width={salaryBarWidth}
              countryCode={countryCode}
            />
          </div>
          {role?.jobPostings && (
            <Typography2 component="p" variant="bodySecondary" color="supportText">
              <FormattedHTMLMessage
                message={_t('Job openings: <strong>{numberOfPostings}<sup>**</sup></strong>')}
                numberOfPostings={<FormattedNumber value={role.jobPostings} />}
              />
            </Typography2>
          )}
          {role?.projected10YearGrowth && (
            <Typography2 component="p" variant="bodySecondary" color="supportText">
              <FormattedHTMLMessage
                message={_t('Projected 10 year growth: <strong>{growth}%<sup>***</sup></strong>')}
                growth={
                  role.projected10YearGrowth > 0 ? (
                    <span>
                      +<FormattedNumber value={role.projected10YearGrowth} />
                    </span>
                  ) : (
                    <FormattedNumber value={role.projected10YearGrowth} />
                  )
                }
              />
            </Typography2>
          )}
        </div>
      )}

      {showRecommendations && (
        <div css={styles.recommendationWrapper}>
          <Button variant="ghost" icon={<ChevronNextIcon />} iconPosition="after" edgeAlign="start">
            {_t('See recommendations')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CareerRoleCard;
